/**
 * EVIDENCE — earned drops and the pending claims that wait on them.
 *
 * A drop is earned by play (ADR-0024) and the server holds it as a pending
 * claim, wallet-free. The view owns no data of its own: it reads the drops
 * (V2-L10b, `GET /core/drops`) and the pending claims (V2-L10c,
 * `GET /core/claims?status=pending`) through `coreSession`, renders them, and
 * forwards player intent (`POST /core/claims/:id/claim`). Claiming is an
 * off-chain stub: the server records the entitlement and never mints; minting
 * is L9. If the line is down or the v2 flag is off, the view says so and
 * leaves the player with an empty, honest screen.
 */
import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = {
  loaded: false,
  loading: false,
  error: null,
  notice: null,
  busy: null,
};

let loadPromise = null;

export function _resetEvidenceState(overrides = {}) {
  Object.assign(
    state,
    { loaded: false, loading: false, error: null, notice: null, busy: null },
    overrides,
  );
  loadPromise = null;
}

export function evidenceErrorMessage(error) {
  if (!error) return 'EVIDENCE LINE DOWN';
  if (error.code === 'CORE-404-012') return 'EVIDENCE LINE CLOSED // V2 OFFLINE';
  return coreErrorMessage(error);
}

export async function loadEvidenceData() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    state.loading = true;
    state.error = null;
    try {
      const session = useCoreSession.getState();
      // Drops only supply provenance for the claims; a drops failure must not
      // hide an empty claims list.
      await Promise.all([
        session.loadClaims('pending'),
        session.loadDrops().catch(() => []),
      ]);
      state.loaded = true;
    } catch (error) {
      state.error = evidenceErrorMessage(error);
      state.loaded = true;
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

export function mapPendingClaims(claims = []) {
  return (Array.isArray(claims) ? claims : []).filter(
    (claim) => String(claim?.status || 'pending').toLowerCase() === 'pending',
  );
}

export function claimLabel(claim, drops = []) {
  const drop = (Array.isArray(drops) ? drops : []).find(
    (row) => row?.dropId && row.dropId === claim?.dropId,
  );
  const trace = drop?.traceKind || drop?.trigger || drop?.payload?.dropId;
  return String(trace || claim?.dropId || claim?.claimId || 'UNLABELLED').toUpperCase();
}

export function claimRarity(claim, drops = []) {
  const drop = (Array.isArray(drops) ? drops : []).find(
    (row) => row?.dropId && row.dropId === claim?.dropId,
  );
  return String(drop?.rarity || '--').toUpperCase();
}

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

async function claimPending(claim) {
  if (!claim?.claimId || state.busy || state.loading) return;
  state.busy = claim.claimId;
  state.error = null;
  state.notice = 'GRANTING CLAIM...';
  try {
    await useCoreSession.getState().claimPendingClaim(claim.claimId);
    state.notice = `CLAIM GRANTED // ${String(claim.claimId).slice(-8).toUpperCase()}`;
  } catch (error) {
    state.error = evidenceErrorMessage(error);
    state.notice = null;
  } finally {
    state.busy = null;
  }
}

function drawTop(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width);
  text(ctx, 'DOPEOS // EVIDENCE', 8, 4, TERMINAL.TEXT);
  if (!mobile) text(ctx, 'EARNED DROPS // PENDING CLAIMS', 216, 4, TERMINAL.TEXT_DIM);
  text(ctx, online ? 'DROPS' : 'OFFLINE', safe.width - 72, 4, online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawClaims(ctx, x, y, w, h, claims, drops, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'PENDING CLAIMS');
  text(ctx, mobile ? 'TRACE / RARITY' : 'TRACE                 RARITY', x + 10, y + 24, TERMINAL.TEXT_DIM);
  text(ctx, `${String(claims.length).padStart(2, '0')} WAITING`, x + w - 88, y + 24, TERMINAL.TEXT_DIM);
  rule(ctx, x + 8, y + 40, w - 16);

  const rowH = mobile ? 46 : 30;
  const top = y + 52;
  const maxRows = Math.max(1, Math.floor((h - 58) / rowH));
  const visible = claims.slice(0, maxRows);
  const hidden = claims.length - visible.length;

  visible.forEach((claim, index) => {
    const rowY = top + index * rowH;
    const label = claimLabel(claim, drops);
    const rarity = claimRarity(claim, drops);
    const busy = state.busy === claim.claimId;

    if (mobile) {
      text(ctx, `${index + 1} ${label.slice(0, 20)}`, x + 12, rowY, TERMINAL.TEXT);
      text(ctx, `${rarity} // PENDING`, x + 12, rowY + 16, TERMINAL.TEXT_DIM);
    } else {
      const line = `${index + 1}  ${label.slice(0, 18).padEnd(20)} ${rarity.padEnd(8)}`;
      text(ctx, line, x + 12, rowY, TERMINAL.TEXT);
    }

    const bw = mobile ? 72 : 64;
    const bh = mobile ? 30 : 20;
    const bx = x + w - bw - 10;
    const by = rowY - (mobile ? 10 : 4);
    DopeUI.Button(ctx, bx, by, bw, bh, busy ? 'BUSY' : 'CLAIM', busy ? 'disabled' : 'hover');
    if (!busy) {
      interactiveRegistry.register({
        id: `evidence-claim-${claim.claimId}`,
        x: bx,
        y: by,
        w: bw,
        h: bh,
        onClick: () => claimPending(claim),
      });
    }
  });

  if (!claims.length) {
    const message = state.loading || !state.loaded ? 'READING THE LINE...' : 'NO PENDING CLAIMS.';
    text(ctx, message, x + 12, top, TERMINAL.TEXT_DIM);
    if (!state.loading && state.loaded) {
      text(ctx, 'EARNED EVIDENCE WAITS HERE.', x + 12, top + 18, TERMINAL.TEXT_DIM);
      text(ctx, 'NOTHING IS LOST WHILE IT WAITS.', x + 12, top + 36, TERMINAL.TEXT_DIM);
    }
  } else if (hidden > 0) {
    text(ctx, `+${hidden} MORE ON FILE`, x + 12, y + h - 18, TERMINAL.TEXT_DIM);
  }
}

function drawFooter(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  const canRefresh = online && !state.loading;
  const note = state.error || state.notice;

  if (mobile) {
    const buttonY = 530;
    const backY = 584;
    if (note) text(ctx, `// ${note}`.slice(0, 38), 8, buttonY - 16, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN);
    DopeUI.Button(ctx, 8, buttonY, safe.width - 16, 44, state.loading ? 'READING...' : 'REFRESH', canRefresh ? 'hover' : 'disabled');
    if (canRefresh) {
      interactiveRegistry.register({
        id: 'evidence-refresh',
        x: 8,
        y: buttonY,
        w: safe.width - 16,
        h: 44,
        onClick: () => loadEvidenceData(),
      });
    }
    DopeUI.Button(ctx, 8, backY, safe.width - 16, 44, 'BACK TO DESKTOP', 'normal');
    interactiveRegistry.register({
      id: 'evidence-back',
      x: 8,
      y: backY,
      w: safe.width - 16,
      h: 44,
      onClick: () => navigateTo('main_menu'),
    });
    return;
  }

  rule(ctx, 0, 320, safe.width);
  text(ctx, 'CLAIM FILED. MINTING WAITS.', 8, 330, TERMINAL.TEXT_DIM);
  DopeUI.Button(ctx, 8, 344, 304, 28, state.loading ? 'READING...' : 'REFRESH', canRefresh ? 'hover' : 'disabled');
  if (canRefresh) {
    interactiveRegistry.register({
      id: 'evidence-refresh',
      x: 8,
      y: 344,
      w: 304,
      h: 28,
      onClick: () => loadEvidenceData(),
    });
  }
  DopeUI.Button(ctx, 320, 344, safe.width - 328, 28, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'evidence-back',
    x: 320,
    y: 344,
    w: safe.width - 328,
    h: 28,
    onClick: () => navigateTo('main_menu'),
  });
  if (note) text(ctx, `// ${note}`.slice(0, 56), 8, 384, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN);
}

registerView('evidence', function renderEvidence(ctx, time, width, height) {
  interactiveRegistry.clear();
  const session = useCoreSession.getState();
  if (session.status === 'online' && session.token && !state.loaded && !loadPromise) {
    loadEvidenceData();
  }

  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);

  drawTop(ctx, safe, session);

  const mobile = safe.width < 500;
  const claims = mapPendingClaims(session.claims);
  const drops = session.drops || [];

  if (session.status !== 'online') {
    DopeUI.Panel(ctx, 8, 36, safe.width - 16, 120, 'NO LINE');
    text(ctx, 'LINE DOWN. LOCAL SHELL.', 20, 70, TERMINAL.TEXT_DIM);
    text(ctx, 'THE PENDING CLAIMS WAIT.', 20, 92, TERMINAL.TEXT_DIM);
  } else if (mobile) {
    drawClaims(ctx, 8, 32, safe.width - 16, 470, claims, drops, true);
  } else {
    drawClaims(ctx, 8, 40, 584, 264, claims, drops);
  }

  drawFooter(ctx, safe, session);
});

export default 'evidence';
